import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '@/lib/firebase';
import { collection, getDocs, doc, updateDoc, query, where, addDoc } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Music2, FileText, BookOpen, PlayCircle, ListMusic, Target, LogOut, Home, Calendar, ExternalLink, Star, Trophy, Sparkles, CheckCircle2, Award, Download, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

function ProgressRing({ progress, size = 80, strokeWidth = 6, color = '#4B1D5E' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="#F5F0F7" strokeWidth={strokeWidth} fill="none" />
      <circle cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth={strokeWidth} fill="none"
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-700" />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        className="fill-[#2D1B36] font-bold text-sm" transform={`rotate(90 ${size / 2} ${size / 2})`}>
        {progress}%
      </text>
    </svg>
  );
}

// Fun achievement badges based on progress
function getAchievementLevel(progress) {
  if (progress >= 100) return { icon: Trophy, label: 'Mastered!', color: '#FFD700', bg: 'bg-yellow-50' };
  if (progress >= 75) return { icon: Star, label: 'Almost there!', color: '#4B1D5E', bg: 'bg-purple-50' };
  if (progress >= 50) return { icon: Sparkles, label: 'Making progress!', color: '#B088BC', bg: 'bg-purple-50' };
  if (progress >= 25) return { icon: Music2, label: 'Getting started', color: '#8A7E90', bg: 'bg-gray-50' };
  return { icon: Target, label: 'New goal', color: '#8A7E90', bg: 'bg-gray-50' };
}

// Fun gradient colors for goal cards
const goalColors = [
  { from: '#4B1D5E', to: '#B088BC' },
  { from: '#6B21A8', to: '#A855F7' },
  { from: '#7C3AED', to: '#C4B5FD' },
  { from: '#8B5CF6', to: '#DDD6FE' },
];

export default function StudentPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [studentId, setStudentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState({
    homework: [],
    notes: [],
    videos: [],
    repertoire: [],
    goals: [],
  });
  const [certificates, setCertificates] = useState([]);
  const [songRequestForm, setSongRequestForm] = useState({ title: '', artist: '', youtubeUrl: '', notes: '' });
  const [submittingSong, setSubmittingSong] = useState(false);

  useEffect(() => {
    if (user?.email) {
      loadStudentData();
    }
  }, [user]);

  const loadStudentData = async () => {
    try {
      // Find student document by email
      const studentsSnap = await getDocs(query(collection(db, 'students'), where('email', '==', user.email.toLowerCase())));
      if (studentsSnap.empty) {
        setLoading(false);
        return;
      }
      
      const studentDoc = studentsSnap.docs[0];
      const sid = studentDoc.id;
      setStudentId(sid);

      // Load student-specific data
      const hwSnap = await getDocs(collection(db, 'students', sid, 'homework'));
      const notesSnap = await getDocs(collection(db, 'students', sid, 'notes'));
      const videosSnap = await getDocs(collection(db, 'students', sid, 'videos'));
      const repSnap = await getDocs(collection(db, 'students', sid, 'repertoire'));
      const goalsSnap = await getDocs(collection(db, 'students', sid, 'goals'));
      const certsSnap = await getDocs(collection(db, 'students', sid, 'certificates'));
      
      setStudentData({
        homework: hwSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        notes: notesSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        videos: videosSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        repertoire: repSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        goals: goalsSnap.docs.map(d => ({ ...d.data(), id: d.id })),
      });
      setCertificates(certsSnap.docs.map(d => ({ ...d.data(), id: d.id })));
    } catch (e) {
      console.error('Error loading student data:', e);
    }
    setLoading(false);
  };

  const toggleHomeworkComplete = async (hwId, currentStatus) => {
    if (!studentId) return;
    try {
      await updateDoc(doc(db, 'students', studentId, 'homework', hwId), { completed: !currentStatus });
      setStudentData(prev => ({
        ...prev,
        homework: prev.homework.map(hw => hw.id === hwId ? { ...hw, completed: !currentStatus } : hw),
      }));
    } catch (e) {
      console.error('Error updating homework:', e);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const submitSongRequest = async (e) => {
    e.preventDefault();
    if (!studentId || !songRequestForm.title.trim()) return;
    setSubmittingSong(true);
    try {
      await addDoc(collection(db, 'students', studentId, 'songRequests'), {
        ...songRequestForm,
        createdAt: new Date().toISOString(),
      });
      setSongRequestForm({ title: '', artist: '', youtubeUrl: '', notes: '' });
      alert('Song request submitted! Your teacher will see it.');
    } catch (e) {
      console.error('Error submitting song request:', e);
      alert('Failed to submit request. Please try again.');
    }
    setSubmittingSong(false);
  };

  const overallProgress = studentData.goals.length > 0 
    ? Math.round(studentData.goals.reduce((a, g) => a + (g.progress || 0), 0) / studentData.goals.length) 
    : 0;

  const pendingHomework = studentData.homework.filter(hw => !hw.completed).length;

  // Convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  return (
    <div data-testid="student-portal" className="min-h-screen bg-[#FAFAFA]">
      {/* Portal Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/">
              <img src="/logo.jpg" alt="Singvolve" className="h-8 w-auto" />
            </Link>
            <Badge variant="outline" className="border-[#B088BC] text-[#4B1D5E] text-xs">Student Portal</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#5E4B66] hidden sm:block">{user?.email}</span>
            <Link to="/">
              <Button variant="ghost" size="icon" className="text-[#5E4B66]" data-testid="portal-home-btn"><Home className="h-4 w-4" /></Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-[#5E4B66]" data-testid="portal-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4B1D5E]"></div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <TabsList className="bg-white border border-purple-100 rounded-xl p-1 flex flex-wrap gap-1 h-auto" data-testid="portal-tabs">
              {[
                { value: 'dashboard', icon: Music2, label: 'Dashboard' },
                { value: 'notes', icon: FileText, label: 'Lesson Notes' },
                { value: 'homework', icon: BookOpen, label: 'Homework' },
                { value: 'youtube', icon: PlayCircle, label: 'Videos' },
                { value: 'repertoire', icon: ListMusic, label: 'Repertoire' },
                { value: 'goals', icon: Target, label: 'Goals' },
                { value: 'certificates', icon: Award, label: 'Certificates' },
                { value: 'request', icon: Send, label: 'Request Song' },
              ].map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-[#4B1D5E] data-[state=active]:text-white" data-testid={`portal-tab-${tab.value}`}>
                  <tab.icon className="h-4 w-4 mr-2" />{tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="dashboard">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm col-span-full lg:col-span-2">
                  <CardContent className="p-8">
                    <h2 className="font-heading text-2xl font-bold text-[#2D1B36] mb-2">Welcome back!</h2>
                    <p className="text-[#5E4B66]">Here's an overview of your singing journey. Keep up the great work!</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-8 flex flex-col items-center">
                    <ProgressRing progress={overallProgress} size={100} />
                    <p className="font-heading font-semibold text-[#2D1B36] mt-4">Overall Progress</p>
                    <p className="text-xs text-[#8A7E90]">{studentData.goals.length} active goals</p>
                  </CardContent>
                </Card>
                {[
                  { label: 'Lesson Notes', count: studentData.notes.length, icon: FileText },
                  { label: 'Pending Homework', count: pendingHomework, icon: BookOpen },
                  { label: 'Active Goals', count: studentData.goals.length, icon: Target },
                ].map((stat) => (
                  <Card key={stat.label} className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-[#F5F0F7] flex items-center justify-center">
                        <stat.icon className="h-6 w-6 text-[#4B1D5E]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#2D1B36]">{stat.count}</p>
                        <p className="text-xs text-[#8A7E90]">{stat.label}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="notes">
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Lesson Notes</h2>
                {studentData.notes.sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((note) => (
                  <Card key={note.id} className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-[#2D1B36]">{note.title}</h3>
                        <div className="flex items-center gap-1 text-xs text-[#8A7E90]">
                          <Calendar className="h-3 w-3" />
                          {note.date}
                        </div>
                      </div>
                      <p className="text-sm text-[#5E4B66] whitespace-pre-wrap">{note.content}</p>
                    </CardContent>
                  </Card>
                ))}
                {studentData.notes.length === 0 && <p className="text-[#8A7E90] text-sm">No lesson notes yet. Your teacher will add notes after each lesson.</p>}
              </div>
            </TabsContent>

            <TabsContent value="homework">
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Homework</h2>
                {studentData.homework.map((hw) => (
                  <Card key={hw.id} className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                    <CardContent className="p-6 flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <button
                          type="button"
                          onClick={() => toggleHomeworkComplete(hw.id, hw.completed)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all cursor-pointer ${
                            hw.completed ? 'bg-[#4B1D5E] border-[#4B1D5E]' : 'border-gray-300 hover:border-[#4B1D5E]'
                          }`}
                          data-testid={`homework-toggle-${hw.id}`}
                        >
                          {hw.completed && <CheckCircle2 className="h-4 w-4 text-white" />}
                        </button>
                        <div>
                          <p className={`font-medium text-sm ${hw.completed ? 'line-through text-[#8A7E90]' : 'text-[#2D1B36]'}`}>{hw.title}</p>
                          {hw.due && <p className="text-xs text-[#8A7E90]">Due: {hw.due}</p>}
                        </div>
                      </div>
                      {hw.completed && <Badge className="bg-green-100 text-green-700 text-xs">Done!</Badge>}
                    </CardContent>
                  </Card>
                ))}
                {studentData.homework.length === 0 && <p className="text-[#8A7E90] text-sm">No homework assigned yet. Check back after your next lesson!</p>}
              </div>
            </TabsContent>

            <TabsContent value="youtube">
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Shared Videos</h2>
                {studentData.videos.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {studentData.videos.map((video) => (
                      <Card key={video.id} className="bg-white border border-purple-100 rounded-2xl shadow-sm overflow-hidden">
                        <div className="aspect-video">
                          <iframe
                            src={getYouTubeEmbedUrl(video.url)}
                            title={video.title}
                            className="w-full h-full"
                            allowFullScreen
                            loading="lazy"
                          />
                        </div>
                        <CardContent className="p-4">
                          <p className="font-medium text-sm text-[#2D1B36]">{video.title}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#8A7E90]">Your teacher will share helpful videos here. Check back soon!</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="repertoire">
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Repertoire List</h2>
                {studentData.repertoire.length > 0 ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {studentData.repertoire.map((song) => (
                      <Card key={song.id} className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                        <CardContent className="p-5">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#F5F0F7] flex items-center justify-center shrink-0">
                              <Music2 className="h-5 w-5 text-[#4B1D5E]" />
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-[#2D1B36]">{song.title}</p>
                              {song.artist && <p className="text-xs text-[#8A7E90]">{song.artist}</p>}
                              {song.lyricsUrl && (
                                <a 
                                  href={song.lyricsUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center gap-1 text-xs text-[#4B1D5E] hover:underline mt-2"
                                >
                                  <ExternalLink className="h-3 w-3" /> View lyrics
                                </a>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#8A7E90]">Songs you're working on will appear here.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="goals">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold text-[#2D1B36]">Your Goals</h2>
                  {overallProgress > 0 && (
                    <div className="flex items-center gap-2 text-sm text-[#4B1D5E]">
                      <Sparkles className="h-4 w-4" />
                      <span className="font-medium">{overallProgress}% overall</span>
                    </div>
                  )}
                </div>
                
                {studentData.goals.length > 0 ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {studentData.goals.map((goal, index) => {
                      const achievement = getAchievementLevel(goal.progress || 0);
                      const colorSet = goalColors[index % goalColors.length];
                      
                      return (
                        <motion.div
                          key={goal.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Card className={`border-0 rounded-2xl shadow-lg overflow-hidden ${achievement.bg}`}>
                            <div 
                              className="h-2" 
                              style={{ 
                                background: `linear-gradient(to right, ${colorSet.from}, ${colorSet.to})`,
                                width: `${goal.progress || 0}%`,
                                transition: 'width 0.5s ease-out'
                              }} 
                            />
                            <CardContent className="p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <h3 className="font-heading font-semibold text-[#2D1B36] mb-1">{goal.title}</h3>
                                  <div className="flex items-center gap-2">
                                    <achievement.icon className="h-4 w-4" style={{ color: achievement.color }} />
                                    <span className="text-xs font-medium" style={{ color: achievement.color }}>{achievement.label}</span>
                                  </div>
                                </div>
                                <ProgressRing progress={goal.progress || 0} size={60} strokeWidth={5} color={colorSet.from} />
                              </div>
                              <div className="w-full bg-white/50 rounded-full h-3 overflow-hidden">
                                <motion.div 
                                  className="h-full rounded-full"
                                  style={{ background: `linear-gradient(to right, ${colorSet.from}, ${colorSet.to})` }}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${goal.progress || 0}%` }}
                                  transition={{ duration: 0.8, delay: index * 0.1 + 0.2 }}
                                />
                              </div>
                              <p className="text-xs text-[#5E4B66] mt-2 text-center">{goal.progress || 0}% complete</p>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-[#F5F0F7] flex items-center justify-center mx-auto mb-4">
                        <Target className="h-8 w-8 text-[#B088BC]" />
                      </div>
                      <h3 className="font-heading font-semibold text-[#2D1B36] mb-2">No goals yet</h3>
                      <p className="text-sm text-[#8A7E90]">Your teacher will set goals for you. These will help track your singing progress!</p>
                    </CardContent>
                  </Card>
                )}

                {studentData.goals.length > 0 && overallProgress >= 50 && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center p-6 bg-gradient-to-r from-[#F5F0F7] to-[#EDE9F0] rounded-2xl"
                  >
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Star className="h-5 w-5 text-[#FFD700]" />
                      <span className="font-heading font-semibold text-[#2D1B36]">You're doing amazing!</span>
                      <Star className="h-5 w-5 text-[#FFD700]" />
                    </div>
                    <p className="text-sm text-[#5E4B66]">Keep up the great work - you're making fantastic progress!</p>
                  </motion.div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="certificates">
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold text-[#2D1B36]">My Certificates</h2>
                {certificates.length > 0 ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {certificates.sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((cert) => (
                      <motion.div key={cert.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <Card className="bg-gradient-to-br from-[#FFD700]/10 to-[#FFF8DC] border-2 border-[#FFD700]/30 rounded-2xl shadow-lg overflow-hidden">
                          <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 rounded-full bg-[#FFD700]/20 flex items-center justify-center mx-auto mb-4">
                              <Award className="h-8 w-8 text-[#FFD700]" />
                            </div>
                            <h3 className="font-heading font-semibold text-[#2D1B36] mb-1">{cert.title}</h3>
                            <p className="text-xs text-[#8A7E90] mb-4">{cert.date}</p>
                            <a href={cert.url} target="_blank" rel="noopener noreferrer">
                              <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg" data-testid={`cert-download-${cert.id}`}>
                                <Download className="h-4 w-4 mr-2" /> View Certificate
                              </Button>
                            </a>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-[#F5F0F7] flex items-center justify-center mx-auto mb-4">
                        <Award className="h-8 w-8 text-[#B088BC]" />
                      </div>
                      <h3 className="font-heading font-semibold text-[#2D1B36] mb-2">No certificates yet</h3>
                      <p className="text-sm text-[#8A7E90]">Certificates you earn will appear here. Keep practicing!</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="request">
              <div className="max-w-xl mx-auto">
                <h2 className="font-heading text-xl font-bold text-[#2D1B36] mb-2">Request a Song</h2>
                <p className="text-sm text-[#8A7E90] mb-6">Is there a song you'd love to learn? Let your teacher know!</p>
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm">
                  <CardContent className="p-6">
                    <form onSubmit={submitSongRequest} className="space-y-4">
                      <div>
                        <Label>Song Title *</Label>
                        <Input 
                          value={songRequestForm.title} 
                          onChange={(e) => setSongRequestForm({ ...songRequestForm, title: e.target.value })} 
                          required 
                          placeholder="e.g. Shallow" 
                          data-testid="song-request-title" 
                        />
                      </div>
                      <div>
                        <Label>Artist (optional)</Label>
                        <Input 
                          value={songRequestForm.artist} 
                          onChange={(e) => setSongRequestForm({ ...songRequestForm, artist: e.target.value })} 
                          placeholder="e.g. Lady Gaga & Bradley Cooper" 
                          data-testid="song-request-artist" 
                        />
                      </div>
                      <div>
                        <Label>YouTube Link (optional)</Label>
                        <Input 
                          value={songRequestForm.youtubeUrl} 
                          onChange={(e) => setSongRequestForm({ ...songRequestForm, youtubeUrl: e.target.value })} 
                          placeholder="https://www.youtube.com/watch?v=..." 
                          data-testid="song-request-youtube" 
                        />
                        <p className="text-xs text-[#8A7E90] mt-1">Share a link to the song so your teacher knows which version you like!</p>
                      </div>
                      <div>
                        <Label>Why do you want to learn this song? (optional)</Label>
                        <Textarea 
                          value={songRequestForm.notes} 
                          onChange={(e) => setSongRequestForm({ ...songRequestForm, notes: e.target.value })} 
                          rows={3} 
                          placeholder="e.g. It's my favourite song, or I'd like to sing it at a family event..." 
                          data-testid="song-request-notes" 
                        />
                      </div>
                      <Button 
                        type="submit" 
                        disabled={submittingSong || !songRequestForm.title.trim()} 
                        className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-lg py-5 text-lg"
                        data-testid="song-request-submit"
                      >
                        <Send className="h-5 w-5 mr-2" /> {submittingSong ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
